import React, { useState, useEffect } from "react";
import { Row, Col, Input, Label, Button } from "reactstrap";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import ProporcionModal from "../../../ProporcionModal";
import ArticuloRapidoModal from "../../../ArticuloRapidoModal";

// Edicion completa de una orden de compra en una sola pantalla: arriba los datos
// de la orden (fecha, proveedor, moneda, plazo, compromiso, folio, observaciones)
// y abajo sus modelos con la misma captura que el editor de pedidos: un bloque
// por modelo (su renglon) con la reja color x talla y su boton de proporcion.
//
// - Las celdas que ya existian se editan sobre su renglon de ArticulosOC; no
//   pueden quedar por debajo de lo ya embarcado.
// - Las celdas nuevas (otro color u otra talla, o un modelo agregado) se dan de
//   alta al guardar, con el numero de renglon de su bloque.
// - Una celda en cero sin nada embarcado se borra; un modelo sin nada embarcado
//   se puede quitar completo.
const URL_ARTICULOS_OC = process.env.REACT_APP_URL_ARTICULOS_OC;
const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const llave = (c, t) => `${c}|${t}`;
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const dinero = (n) => `$${new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
const redondea = (n) => Number((Number(n) || 0).toFixed(2));

const resuelve = (lista, catalogo) => {
  if (!lista || !lista.length) return [...catalogo];
  return lista.map((x) => (x && x.name ? x : catalogo.find((c) => String(c._id) === String(x && x._id ? x._id : x)))).filter(Boolean);
};
// Piso de una linea: no puede bajar de lo embarcado (si un dato viejo ya traia
// mas embarcado que cantidad, el piso es la cantidad que tenia).
const pisoLinea = (l) => Math.min(l.embarcado || 0, l.cantidad || 0);

function reparteEnLineas(lineas, total) {
  const nuevas = lineas.map((l) => pisoLinea(l));
  let resto = total - nuevas.reduce((s, n) => s + n, 0);
  lineas.forEach((l, i) => {
    const sube = Math.max(0, Math.min(resto, (l.cantidad || 0) - nuevas[i]));
    nuevas[i] += sube; resto -= sube;
  });
  if (resto > 0) nuevas[0] += resto;
  return nuevas;
}

const agrega = (lista, item) => (item && !lista.some((x) => String(x._id) === String(item._id)) ? [...lista, item] : lista);

function EditarOrdenCompra({ orden, articulos, coloresCat, tallasCat, proveedores, user, onRegresar }) {
  // datos de la orden
  const [fecha, setFecha] = useState(orden.fecha || "");
  const [proveedor, setProveedor] = useState(orden.idProveedores || "");
  const [moneda, setMoneda] = useState(orden.moneda || "USD");
  const [plazo, setPlazo] = useState(orden.plazo ?? 0);
  const [compromiso, setCompromiso] = useState(orden.fecha_compromiso || "");
  const [folio, setFolio] = useState(orden.folioProveedor || "");
  const [observaciones, setObservaciones] = useState(orden.observaciones && orden.observaciones !== "NA" ? orden.observaciones : "");

  const [bloques, setBloques] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [colores, setColores] = useState(coloresCat || []);
  const [tallas, setTallas] = useState(tallasCat || []);
  const [propBloque, setPropBloque] = useState(null);
  const [altaBloque, setAltaBloque] = useState(null);
  const [extraArts, setExtraArts] = useState([]);

  useEffect(() => { setColores(coloresCat || []); }, [coloresCat]);
  useEffect(() => { setTallas(tallasCat || []); }, [tallasCat]);

  const bloqueDe = (art, renglon, nuevo) => ({
    key: uuidv4(), nuevo, renglon, quitar: false,
    articulo: art?._id || "", nombre: art ? `${art.codigo} ${art.nombre}` : "",
    precio: 0, precioOriginal: null,
    colores: resuelve(art?.colores, colores), tallas: resuelve(art?.tallas, tallas), celdas: {},
  });

  useEffect(() => {
    setCargando(true);
    axios.get(`${URL_ARTICULOS_OC}/ordenesCompra/${orden._id}`, { headers: headers() })
      .then((r) => {
        const lineas = (r.data || []).filter((a) => a.is_active !== "No" && a.articulos?.[0]?._id);
        const porBloque = new Map();
        lineas.forEach((l) => {
          const art = l.articulos[0];
          const kb = `${art._id}|${l.renglon || 1}`;
          if (!porBloque.has(kb)) {
            const delCatalogo = articulos.find((a) => String(a._id) === String(art._id)) || art;
            const b = bloqueDe(delCatalogo, l.renglon || 1, false);
            b.nombre = `${art.codigo} ${art.nombre}`;
            b.precio = Number(l.precio) || 0;
            b.precioOriginal = b.precio;
            porBloque.set(kb, b);
          }
          const b = porBloque.get(kb);
          const col = l.colores?.[0], tal = l.tallas?.[0];
          b.colores = agrega(b.colores, col);
          b.tallas = agrega(b.tallas, tal);
          const k = llave(col?._id, tal?._id);
          const linea = { id: l._id, cantidad: Number(l.cantidad) || 0, embarcado: Number(l.embarcado) || 0, precio: Number(l.precio) || 0 };
          const c = b.celdas[k] || { lineas: [], cantidad: 0, embarcado: 0, minimo: 0 };
          c.lineas.push(linea);
          c.cantidad += linea.cantidad;
          c.embarcado += linea.embarcado; c.minimo += pisoLinea(linea);
          b.celdas[k] = c;
        });
        setBloques([...porBloque.values()].sort((a, b) => a.renglon - b.renglon));
        setCargando(false);
      })
      .catch((e) => {
        setCargando(false);
        Swal.fire({ icon: "error", title: "No se pudieron traer los artículos de la orden", text: e.message });
      });
  }, [orden._id]); // eslint-disable-line

  const cambiaBloque = (key, cambio) => setBloques((prev) => prev.map((b) => (b.key === key ? { ...b, ...cambio(b) } : b)));

  const cambiaCelda = (key, idC, idT, valor) =>
    cambiaBloque(key, (b) => {
      const k = llave(idC, idT);
      const actual = b.celdas[k] || { cantidad: 0, embarcado: 0, minimo: 0 };
      return { celdas: { ...b.celdas, [k]: { ...actual, cantidad: valor === "" ? "" : Number(valor) } } };
    });

  function aplicaProporcion(lineas) {
    let respetadas = 0;
    cambiaBloque(propBloque, (b) => {
      const celdas = { ...b.celdas };
      b.colores.forEach((c) => b.tallas.forEach((t) => {
        const k = llave(c._id, t._id);
        const l = lineas.find((x) => x.colores === c._id && x.tallas === t._id);
        const actual = celdas[k] || { cantidad: 0, embarcado: 0, minimo: 0 };
        let cantidad = l ? l.cantidad : 0;
        if (cantidad < (actual.minimo || 0)) { cantidad = actual.minimo; respetadas += 1; }
        if (celdas[k] || cantidad > 0) celdas[k] = { ...actual, cantidad };
      }));
      return { celdas };
    });
    if (respetadas > 0) {
      setTimeout(() => Swal.fire({ icon: "info", title: "Se respetó lo ya embarcado",
        text: `${respetadas} celda(s) quedaron en lo que ya se embarcó; no pueden bajar de ahí.` }), 300);
    }
  }

  const agregaModelo = () => setBloques((prev) => [...prev, bloqueDe(null, 0, true)]);

  const eligeModelo = (key, art) => {
    if (!art) return;
    const nuevo = bloqueDe(art, 0, true);
    cambiaBloque(key, () => ({ articulo: nuevo.articulo, nombre: nuevo.nombre, colores: nuevo.colores, tallas: nuevo.tallas, celdas: {} }));
  };

  const quitaBloque = (b) => {
    if (b.nuevo) { setBloques((prev) => prev.filter((x) => x.key !== b.key)); return; }
    cambiaBloque(b.key, (x) => ({ quitar: !x.quitar }));
  };

  // Precio de una linea existente: si se cambio el precio del modelo, el nuevo
  // para todas; si no, el que ya traia cada una.
  const precioLinea = (b, l) => (b.precioOriginal !== null && Number(b.precio) === b.precioOriginal ? l.precio : Number(b.precio) || 0);
  const vivos = bloques.filter((b) => !b.quitar && b.articulo);
  const piezasBloque = (b) => Object.values(b.celdas).reduce((s, c) => s + (Number(c.cantidad) || 0), 0);
  const importeBloque = (b) => Object.values(b.celdas).reduce((s, c) => {
    const q = Number(c.cantidad) || 0;
    if (!c.lineas) return s + q * (Number(b.precio) || 0);
    const nuevas = reparteEnLineas(c.lineas, q);
    return s + c.lineas.reduce((x, l, i) => x + nuevas[i] * precioLinea(b, l), 0);
  }, 0);
  const piezas = vivos.reduce((s, b) => s + piezasBloque(b), 0);
  const importe = vivos.reduce((s, b) => s + importeBloque(b), 0);
  const embarcadoTotal = bloques.reduce((s, b) => s + Object.values(b.celdas).reduce((x, c) => x + (c.embarcado || 0), 0), 0);
  // con algo embarcado la moneda ya no se cambia: los embarques se costearon en ella
  const yaEmbarcado = Number(orden.embarcado) > 0 || embarcadoTotal > 0;

  async function guardar() {
    const faltan = [];
    if (!fecha) faltan.push("fecha");
    if (!proveedor || proveedor === "0") faltan.push("proveedor");
    if (!compromiso) faltan.push("fecha compromiso");
    if (faltan.length) {
      Swal.fire({ icon: "warning", title: "Faltan datos de la orden", text: `Captura: ${faltan.join(", ")}.` });
      return;
    }
    const bajo = [];
    vivos.forEach((b) => Object.values(b.celdas).forEach((c) => {
      if ((Number(c.cantidad) || 0) < (c.minimo || 0)) bajo.push(b.nombre);
    }));
    if (bajo.length) {
      Swal.fire({ icon: "warning", title: "Por debajo de lo embarcado", text: `Revisa ${[...new Set(bajo)].join(", ")}: una cantidad quedó por debajo de lo ya embarcado.` });
      return;
    }
    if (bloques.some((b) => b.nuevo && !b.articulo)) {
      Swal.fire({ icon: "warning", title: "Falta el modelo", text: "Hay un renglón nuevo sin modelo: elígelo o quítalo." });
      return;
    }
    if (!piezas) {
      Swal.fire({ icon: "warning", title: "La orden queda sin piezas", text: "Si ya no va, cancélala desde el listado." });
      return;
    }
    const conf = await Swal.fire({
      title: `¿Guardar los cambios de la orden ${orden.numero}?`,
      html: `${num(piezas)} piezas · ${dinero(importe)} ${moneda}`,
      icon: "question", showCancelButton: true, confirmButtonText: "Sí, guardar", cancelButtonText: "Cancelar",
    });
    if (!conf.isConfirmed) return;

    setGuardando(true);
    try {
      let ultimo = bloques.reduce((m, b) => Math.max(m, b.renglon || 0), 0);
      for (const b of bloques) {
        if (b.nuevo && b.quitar) continue;
        const renglon = b.nuevo ? (ultimo += 1) : b.renglon;
        for (const [k, c] of Object.entries(b.celdas)) {
          const [idC, idT] = k.split("|");
          const cantidad = Number(c.cantidad) || 0;
          if (c.lineas) {
            const nuevas = b.quitar ? c.lineas.map(() => 0) : reparteEnLineas(c.lineas, cantidad);
            for (const [i, l] of c.lineas.entries()) {
              const q = nuevas[i];
              const precio = precioLinea(b, l);
              // modelo quitado, o linea en cero sin nada embarcado: se borra
              if (q === 0 && !l.embarcado) {
                await axios.delete(`${URL_ARTICULOS_OC}/${l.id}`, { headers: headers() });
              } else if (q !== l.cantidad || precio !== l.precio) {
                await axios.patch(`${URL_ARTICULOS_OC}/${l.id}`, {
                  cantidad: q, precio, total: redondea(q * precio), pendiente_embarcar: Math.max(0, q - (l.embarcado || 0)),
                }, { headers: headers() });
              }
            }
          } else if (!b.quitar && cantidad > 0) {
            const precio = Number(b.precio) || 0;
            await axios.post(URL_ARTICULOS_OC, {
              ordenesCompra: orden._id, renglon, articulos: b.articulo, colores: idC, tallas: idT,
              cantidad, embarcado: 0, pendiente_embarcar: cantidad, precio, total: redondea(cantidad * precio),
            }, { headers: headers() });
          }
        }
      }
      const datos = {
        fecha, proveedores: proveedor, moneda, plazo: Number(plazo) || 0, fecha_compromiso: compromiso,
        observaciones: observaciones.trim() || "NA",
        total_cantidad: piezas, total_general: redondea(importe),
      };
      if (folio.trim()) datos.folioProveedor = folio.trim();
      await axios.patch(`${URL_ORDENES_COMPRA}/${orden._id}`, datos, { headers: headers() });
      await axios.post(URL_LOGS, { tipo: "Editar Orden de Compra", detalle: `${orden.numero}`, user: user.id }, { headers: headers() }).catch(() => {});
      setGuardando(false);
      await Swal.fire({ icon: "success", title: "Orden actualizada", timer: 1200, showConfirmButton: false });
      window.location.reload();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: error.response?.data?.message || error.message,
        footer: "Lo que alcanzó a guardarse ya quedó; revisa la orden antes de volver a guardar." });
    }
  }

  const bloqueProp = bloques.find((b) => b.key === propBloque);
  const opciones = [...articulos, ...extraArts].sort((a, b) => (a.codigo > b.codigo ? 1 : -1));

  return (
    <div className="card container col-md-12">
      <h4>Editar orden de compra {orden.numero}</h4>

      <Row>
        <Col md={2}>
          <Label>Fecha</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </Col>
        <Col md={3}>
          <Label>Proveedor</Label>
          <Input type="select" value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
            <option value="0">Selecciona un proveedor</option>
            {[...proveedores].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.nombre_comercial}</option>
            ))}
          </Input>
        </Col>
        <Col md={2}>
          <Label>Moneda</Label>
          <Input type="select" value={moneda} disabled={yaEmbarcado}
            title={yaEmbarcado ? "Ya tiene embarques: la moneda ya no se cambia" : undefined}
            onChange={(e) => setMoneda(e.target.value)}>
            <option value="USD">USD</option>
            <option value="CNY">CNY</option>
            <option value="MXN">MXN</option>
          </Input>
        </Col>
        <Col md={2}>
          <Label>Plazo</Label>
          <Input type="number" min="0" value={plazo} onChange={(e) => setPlazo(e.target.value)} />
        </Col>
        <Col md={3}>
          <Label>Fecha compromiso</Label>
          <Input type="date" value={compromiso} onChange={(e) => setCompromiso(e.target.value)} />
        </Col>
      </Row>
      <Row style={{ marginTop: 8 }}>
        <Col md={3}>
          <Label>Folio del proveedor</Label>
          <Input value={folio} onChange={(e) => setFolio(e.target.value)} />
        </Col>
        <Col md={4}>
          <Label>Observaciones</Label>
          <Input type="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </Col>
        <Col md={5} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ width: 120 }}><Label>Piezas</Label><Input value={num(piezas)} disabled /></div>
          <div style={{ width: 170 }}><Label>Total {moneda}</Label><Input value={dinero(importe)} disabled /></div>
          <div style={{ width: 120 }}><Label>Embarcado</Label><Input value={num(embarcadoTotal)} disabled /></div>
        </Col>
      </Row>

      <h5 style={{ marginTop: 18 }}>Modelos</h5>
      {cargando ? <p style={{ color: "#64748b" }}>Cargando los artículos de la orden...</p> : null}

      {bloques.map((b) => {
        const embBloque = Object.values(b.celdas).reduce((s, c) => s + (c.embarcado || 0), 0);
        return (
          <div key={b.key} style={{ marginBottom: 14, opacity: b.quitar ? 0.45 : 1 }}>
            <Row className="align-items-end">
              <Col md={3}>
                <Label style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 700 }}>
                  {b.nuevo ? "MODELO NUEVO EN LA ORDEN" : `RENGLON #${b.renglon}`}
                  {b.quitar ? " · SE QUITA AL GUARDAR" : ""}
                </Label>
                {b.nuevo ? (
                  <Autocomplete
                    size="small"
                    options={opciones}
                    value={opciones.find((a) => String(a._id) === String(b.articulo)) || null}
                    onChange={(e, art) => eligeModelo(b.key, art)}
                    getOptionLabel={(o) => `${o.codigo} ${o.nombre}`}
                    renderInput={(params) => <TextField {...params} label="Selecciona el modelo" variant="outlined" />}
                  />
                ) : (
                  <Input value={b.nombre} disabled />
                )}
              </Col>
              <Col md={2}>
                <Label>Precio {moneda}</Label>
                <Input type="number" min="0" step="any" value={b.precio}
                  onChange={(e) => { const v = e.target.value; cambiaBloque(b.key, () => ({ precio: v })); }} />
              </Col>
              <Col md={2}><Label>Piezas</Label><Input value={num(piezasBloque(b))} disabled /></Col>
              <Col md={2}><Label>Importe</Label><Input value={dinero(importeBloque(b))} disabled /></Col>
              <Col md={3} style={{ display: "flex", gap: 6, alignItems: "center", paddingBottom: 4 }}>
                <Button size="sm" color="primary" disabled={!b.articulo || b.quitar}
                  title="Repartir un total entre los colores y tallas del modelo con su proporción (ej. 2-1-1 y 1-2-2-1)"
                  onClick={() => setPropBloque(b.key)}>
                  <i className="fas fa-percent" /> Proporción
                </Button>
                {b.nuevo ? (
                  <Button size="sm" color="warning" title="Dar de alta un modelo que no existe"
                    onClick={() => setAltaBloque(b.key)}><i className="fas fa-plus-square" /></Button>
                ) : null}
                <Button size="sm" color={b.quitar ? "secondary" : "danger"} disabled={embBloque > 0}
                  title={embBloque > 0 ? "Ya tiene piezas embarcadas: no se puede quitar" : b.quitar ? "Dejar el modelo en la orden" : "Quitar este modelo de la orden"}
                  onClick={() => quitaBloque(b)}>
                  <i className={b.quitar ? "fas fa-undo" : "fas fa-minus"} />
                </Button>
              </Col>
            </Row>

            {b.articulo ? (
              <div style={{ margin: "4px 0 0 0", padding: "8px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflowX: "auto" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 4 }}>
                  <div style={{ width: 130 }} />
                  {b.tallas.map((t) => (
                    <div key={t._id} style={{ width: 70, textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#64748b" }}>{t.name}</div>
                  ))}
                  <div style={{ width: 70, textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#64748b" }}>TOTAL</div>
                </div>
                {b.colores.map((col) => {
                  const sumaColor = b.tallas.reduce((s, t) => s + (Number(b.celdas[llave(col._id, t._id)]?.cantidad) || 0), 0);
                  return (
                    <div key={col._id} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <div style={{ width: 130, fontSize: "0.78rem", fontWeight: 600 }}>{col.name}</div>
                      {b.tallas.map((t) => {
                        const c = b.celdas[llave(col._id, t._id)];
                        return (
                          <div key={t._id} style={{ width: 70 }}>
                            <Input bsSize="sm" type="number" min={c?.minimo || 0} disabled={b.quitar}
                              style={{ textAlign: "right", padding: "2px 4px" }}
                              value={c ? c.cantidad : 0}
                              onChange={(e) => cambiaCelda(b.key, col._id, t._id, e.target.value)} />
                            {c?.embarcado ? (
                              <div style={{ fontSize: "0.6rem", textAlign: "right", color: "#b45309" }}>emb. {num(c.embarcado)}</div>
                            ) : null}
                          </div>
                        );
                      })}
                      <div style={{ width: 70, textAlign: "right", fontSize: "0.8rem", fontWeight: 700 }}>{num(sumaColor)}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 8, margin: "6px 0 18px 0" }}>
        <Button color="info" size="sm" onClick={agregaModelo} title="Agregar otro modelo a la orden">
          <i className="fas fa-plus" /> Agregar modelo
        </Button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <Button color="success" onClick={guardar} disabled={guardando || cargando}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
        <Button color="danger" onClick={onRegresar} disabled={guardando}>Regresar</Button>
      </div>

      <ProporcionModal
        abierto={!!propBloque}
        cerrar={() => setPropBloque(null)}
        coloresCat={bloqueProp ? bloqueProp.colores : []}
        tallasCat={bloqueProp ? bloqueProp.tallas : []}
        onAplicar={aplicaProporcion}
        onNuevoColor={(c) => {
          setColores((l) => agrega(l, c));
          cambiaBloque(propBloque, (b) => ({ colores: agrega(b.colores, c) }));
        }}
        onNuevaTalla={(t) => {
          setTallas((l) => agrega(l, t));
          cambiaBloque(propBloque, (b) => ({ tallas: agrega(b.tallas, t) }));
        }}
        catalogoColores={colores}
        catalogoTallas={tallas}
        marcarTodos
        titulo="Proporción del modelo por color y talla"
      />

      <ArticuloRapidoModal
        abierto={!!altaBloque}
        cerrar={() => setAltaBloque(null)}
        coloresCat={colores}
        tallasCat={tallas}
        onCatalogoNuevo={(tipo, nuevo) => (tipo === "color" ? setColores : setTallas)((l) => agrega(l, nuevo))}
        onCreado={(art) => {
          const nuevo = bloqueDe(art, 0, true);
          cambiaBloque(altaBloque, () => ({ articulo: nuevo.articulo, nombre: nuevo.nombre, colores: nuevo.colores, tallas: nuevo.tallas, celdas: {} }));
          setExtraArts((l) => [...l, art]);
        }}
      />
    </div>
  );
}

export default EditarOrdenCompra;
